import React from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Card, Text } from '@zillow/constellation-10';

import { useSelector } from '../../../selectors/useSelector';

import { ZRMOverview } from '../ZRMOverview';
import { NoSearchResults } from '../NoSearchResults';

import GroupablePropertiesViewList from '../GroupablePropertiesViewList';

import type { AccountViewListingGroupDto, ListingCountDto } from '@zg-rentals/ts-rental-manager-api-client';

export type Props = {
  listingGroups: Array<AccountViewListingGroupDto>;
  listingCount?: ListingCountDto;
  isError: boolean;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  onRemoveSearchTextClick?: (value: string) => void;
};

const GroupablePropertiesDisplay = ({
  listingGroups = [],
  listingCount,
  isError,
  isLoading,
  isFetchingNextPage = false,
  onRemoveSearchTextClick,
}: Props) => {
  const { user, propertiesFilters } = useSelector(
    (state) => ({
      user: state.user,
      propertiesFilters: state.properties.propertiesFilters,
    }),
    shallowEqual,
  );

  const showArchived = propertiesFilters.includeArchived;
  const hasAnyArchivedListings = (listingCount?.archived || 0) > 0;

  const handleRemoveSearchTextClick = (value: string) => {
    if (onRemoveSearchTextClick) {
      onRemoveSearchTextClick(value);
    }
  };

  const toggleArchivedView = () => {
    // This will be handled by the parent component via propertiesFilters update
    // The component will automatically re-fetch when propertiesFilters change
  };

  const renderTimedOutDisplay = () => (
    <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginTop: '1200' }}>
      <Text
        css={{
          textAlign: 'center',
          padding: { base: '0 {spacing.400}', lg: '0' },
          marginBottom: '800',
          marginInline: '400',
        }}
      >
        We are having trouble retrieving your properties at this time. Please refresh the page or check back soon.
      </Text>
    </Box>
  );

  const renderNoListingsFound = () => {
    if (propertiesFilters.searchText) {
      return <NoSearchResults showArchived={showArchived} onRemoveSearchTextClick={handleRemoveSearchTextClick} />;
    }

    if (showArchived) {
      return (
        <Text css={{ textAlign: 'center', padding: { base: '0 {spacing.400}', lg: '0' }, marginBottom: '800' }}>
          You don&apos;t have any archived properties.
        </Text>
      );
    }

    if (user.features.canAddListing) {
      return (
        <ZRMOverview hasAnyArchivedListings={hasAnyArchivedListings} handleShowArchivedClick={toggleArchivedView} />
      );
    }

    return (
      <Text css={{ textAlign: 'center', padding: { base: '0 {spacing.400}', lg: '0' }, marginBottom: '800' }}>
        No properties found.
      </Text>
    );
  };

  const renderListingsDisplay = () => (
    <React.Fragment>
      {listingGroups.length > 0 || isLoading ? (
        <Card tone="neutral" elevated={false}>
          <GroupablePropertiesViewList
            listings={listingGroups}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
          />
        </Card>
      ) : (
        <Card
          outlined={false}
          elevated={false}
          css={{
            marginBlock: '1200',
            backgroundColor: 'transparent',
            textAlign: 'center',
            lg: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' },
          }}
        >
          {renderNoListingsFound()}
        </Card>
      )}
    </React.Fragment>
  );

  return (
    <Box data-testid="GroupablePropertiesDisplay">{isError ? renderTimedOutDisplay() : renderListingsDisplay()}</Box>
  );
};

export { GroupablePropertiesDisplay };
export default GroupablePropertiesDisplay;
